/// <reference types="vite/client" />
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Variables d\'environnement VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY requises'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// Helper pour obtenir l'URL publique d'une image dans le storage
export function getStorageUrl(bucket: string, path: string): string {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

// Helper pour uploader une image
function sanitizePathSegment(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export async function uploadImage(
  bucket: string,
  file: File,
  path: string
): Promise<{ url: string | null; error: string | null }> {
  const fileExt = file.name.split('.').pop() || 'bin';
  const baseName = sanitizePathSegment(path || file.name) || 'image';
  const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const fileName = `${baseName}-${uniqueSuffix}.${fileExt}`;

  const { error } = await supabase.storage
    .from(bucket)
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    return { url: null, error: error.message };
  }

  const url = getStorageUrl(bucket, fileName);
  return { url, error: null };
}

// Helper pour supprimer une image
export async function deleteImage(
  bucket: string,
  url: string
): Promise<{ success: boolean; error: string | null }> {
  // Extraire le chemin depuis l'URL complète
  const urlObj = new URL(url);
  const pathParts = urlObj.pathname.split('/storage/v1/object/public/');
  if (pathParts.length !== 2) {
    return { success: false, error: 'URL invalide' };
  }
  
  const fullPath = pathParts[1];
  const bucketAndPath = fullPath.split('/');
  bucketAndPath.shift(); // Enlever le nom du bucket
  const filePath = bucketAndPath.join('/');

  const { error } = await supabase.storage.from(bucket).remove([filePath]);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

export default supabase;
